import { useCallback, useRef, useState } from 'react';
import { fetchProductDetail } from '../api/products';
import type { ProductDetail } from '../api/products';

export interface DetailState {
  loading: boolean;
  error: string | null;
  data: ProductDetail | null;
}

const EMPTY: DetailState = { loading: false, error: null, data: null };

/**
 * Lazily loads item detail for an expanded table row (BOT-392).
 *
 * The review was explicit that detail must load on expand and not be preloaded
 * per row: a page can hold up to 1000 rows and this is one query per item. The
 * result is cached per NS item id for the life of the page, so collapsing and
 * re-opening a row — or opening the same item from two different checks — is free.
 *
 * Keyed on the NS item id rather than the row, so every list view that shows
 * NetSuite items (checks, products) shares one cache and one code path.
 */
export function useItemDetail() {
  const [states, setStates] = useState<Record<string, DetailState>>({});
  // Guards against the expand effect re-firing a request that is already in
  // flight — state updates lag the effect that triggered them.
  const inFlight = useRef<Set<string>>(new Set());

  const loadDetail = useCallback(async (nsItemId: string | number | null | undefined) => {
    if (nsItemId == null) return;
    const key = String(nsItemId);
    if (!key || inFlight.current.has(key)) return;

    let alreadyLoaded = false;
    setStates(prev => {
      if (prev[key]?.data || prev[key]?.loading) { alreadyLoaded = true; return prev; }
      return { ...prev, [key]: { loading: true, error: null, data: null } };
    });
    if (alreadyLoaded) return;

    inFlight.current.add(key);
    try {
      const data = await fetchProductDetail(Number(key));
      setStates(prev => ({ ...prev, [key]: { loading: false, error: null, data } }));
    } catch (e) {
      setStates(prev => ({
        ...prev,
        [key]: { loading: false, error: e instanceof Error ? e.message : 'Failed to load details', data: null },
      }));
    } finally {
      inFlight.current.delete(key);
    }
  }, []);

  const detailFor = useCallback(
    (nsItemId: string | number | null | undefined): DetailState =>
      (nsItemId == null ? EMPTY : states[String(nsItemId)] ?? EMPTY),
    [states],
  );

  return { detailFor, loadDetail };
}
